import { useEffect } from "react";
import { Link } from "react-router-dom";
import MagazineLayout from "@/components/MagazineLayout";
import Seo from "@/components/Seo";
import { Reveal } from "@/components/Reveal";
import { VALUE_STACK, VALUE_TOTAL } from "@/lib/valueStack";
import { ArrowRight, Link2 } from "lucide-react";
import { toast } from "sonner";

export default function MediaKit() {
  useEffect(() => { document.title = "Media Kit — S/ME"; }, []);

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(window.location.href);
      toast.success("Link copied — send it to a business that deserves to be seen.");
    } catch {
      toast.error("Could not copy the link.");
    }
  };

  return (
    <MagazineLayout>
      <Seo
        title="Media Kit — S/ME"
        description="What S/ME membership includes over a year, and what it would cost bought separately. The UK's best-value visibility membership: £30 a month or £300 a year."
      />
      <div className="pt-[68px] bg-cream min-h-screen" data-testid="media-kit-page">
        {/* masthead */}
        <header className="bg-ink text-cream px-5 sm:px-8 py-16 sm:py-24 grain">
          <div className="max-w-[1100px] mx-auto relative z-10">
            <Reveal>
              <p className="overline text-gloss-gold mb-5">Media Kit · Share this page</p>
              <h1 className="font-serif text-5xl sm:text-7xl tracking-tight leading-[0.9]">S/ME, on one page.</h1>
              <p className="font-sans text-lg sm:text-xl text-cream/75 mt-6 max-w-2xl leading-relaxed">
                S/ME is the bold new online magazine for the UK's independent businesses. Members get a year of editorial features,
                photography, adverts, social content, SEO and a permanent directory listing — for £30 a month or £300 a year.
              </p>
              <button onClick={copyLink} className="inline-flex items-center gap-2 mt-7 border border-cream/50 px-6 py-3 overline hover:bg-cream hover:text-ink transition-colors" data-testid="media-kit-copy-link">
                <Link2 size={14} /> Copy link to share
              </button>
            </Reveal>
          </div>
        </header>

        {/* who it's for */}
        <section className="px-5 sm:px-8 py-16 max-w-[1100px] mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-10">
            <Reveal className="border-t-2 border-ink pt-6">
              <h2 className="font-serif text-2xl sm:text-3xl tracking-tight">One magazine, the whole country</h2>
              <p className="font-sans text-base text-ink/75 mt-3 leading-relaxed">
                S/ME members are based all over the UK. Many are destination businesses worth travelling for; many are remote
                or online businesses serving customers anywhere. Area tags are context, not boundaries — every member sits
                together in one national publication, in front of one national readership.
              </p>
            </Reveal>
            <Reveal delay={0.06} className="border-t-2 border-ink pt-6">
              <h2 className="font-serif text-2xl sm:text-3xl tracking-tight">A bundle you can't buy elsewhere</h2>
              <p className="font-sans text-base text-ink/75 mt-3 leading-relaxed">
                No publication, directory or agency offers editorial features, cover photography, adverts, interviews, social
                content, SEO and a directory listing in one membership. The equivalent, bought separately, doesn't exist —
                and assembling it yourself costs thousands.
              </p>
            </Reveal>
          </div>
        </section>

        {/* the maths */}
        <section className="bg-secondary px-5 sm:px-8 py-16">
          <div className="max-w-[1100px] mx-auto">
            <h2 className="font-grotesk text-3xl sm:text-4xl font-medium tracking-tight border-b border-ink/20 pb-5 mb-8">The honest maths</h2>
            <div className="border border-ink/15 bg-cream" data-testid="media-kit-value-table">
              {VALUE_STACK.map((v) => (
                <div key={v.item} className="grid grid-cols-1 sm:grid-cols-12 gap-2 sm:gap-6 px-5 sm:px-7 py-4 border-b border-ink/10 items-baseline">
                  <div className="sm:col-span-7">
                    <p className="font-serif text-lg sm:text-xl tracking-tight">{v.item}</p>
                    <p className="font-sans text-sm text-ink/60">{v.detail}</p>
                  </div>
                  <p className="sm:col-span-3 font-mono text-xs uppercase tracking-wider text-ink/50">{v.basis}</p>
                  <p className="sm:col-span-2 font-serif text-xl sm:text-right">{v.market}</p>
                </div>
              ))}
              <div className="grid grid-cols-1 sm:grid-cols-12 gap-2 sm:gap-6 px-5 sm:px-7 py-6 items-baseline bg-ink text-cream">
                <p className="sm:col-span-10 font-serif text-xl sm:text-2xl tracking-tight">Bought separately, per year</p>
                <p className="sm:col-span-2 font-serif text-2xl sm:text-right text-gloss-gold">{VALUE_TOTAL}</p>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-12 gap-2 sm:gap-6 px-5 sm:px-7 py-6 items-baseline">
                <p className="sm:col-span-10 font-serif text-xl sm:text-2xl tracking-tight">S/ME membership, per year</p>
                <p className="sm:col-span-2 font-serif text-2xl sm:text-right">£300</p>
              </div>
            </div>
          </div>
        </section>

        {/* cta */}
        <section className="bg-ink text-cream px-5 sm:px-8 py-20 grain">
          <div className="max-w-[1100px] mx-auto relative z-10 flex flex-col md:flex-row md:items-center justify-between gap-8">
            <div>
              <h2 className="font-serif text-4xl sm:text-6xl tracking-tight leading-none">£30 a month. £300 a year.</h2>
              <p className="font-sans text-lg text-cream/75 mt-4 max-w-xl">The best-value visibility membership available to UK SMEs.</p>
            </div>
            <Link to="/join" className="inline-flex items-center gap-3 bg-cream text-ink px-8 py-5 overline hover:bg-gloss-gold transition-colors shrink-0" data-testid="media-kit-join">
              Become a member <ArrowRight size={16} />
            </Link>
          </div>
        </section>
      </div>
    </MagazineLayout>
  );
}
